/**
 * The scoring card.
 *
 * One card per foursome, gross strokes only, and anyone in the group can enter
 * or fix any cell — that is how a real scorer's card works, and forcing each
 * player to enter their own would leave holes blank all afternoon.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { clockTime } from '../engine/clock'
import { runMatch } from '../engine/formats'
import { findTee, holesFor } from '../engine/handicap'
import { matchNameIn } from '../engine/naming'
import { MatchRulesFor } from './Rules'
import { matchCounts } from '../engine/types'
import type { HoleScoreValue, RoundContext, Tournament } from '../engine/types'
import { missingHoles } from '../engine/scoring'
import { contestedCells, type ScoreEvent } from '../engine/contested'
import { buildContext, contestOrder, courseOf, currentRoundId, inPlayOrder, playerName, popsRemaining, recalled, recalledRound, remember, rememberRound, takeScoreTarget, roundPickerLabel, roundNet, shortNames, toParLabel, todayLocal } from '../select'
import {
  markMiss,
  call,
  clearScores,
  isPending,
  mergedConfirmations,
  mergedScores,
  setCardConfirmed,
  setScore,
  useStore,
} from '../store'
import { Bar, Empty, UnverifiedBanner } from '../ui'
import { CardPhotoButton } from './CardPhoto'
import { Grid } from './Grid'
import { AddRound } from './AddRound'
import { SideBoard } from './SideBoard'
import { SideMatchStrip } from './SideMatch'

/**
 * The rules of the round you are standing on, one tap away.
 *
 * Scoring is where the question actually gets asked — "do I get a shot here",
 * "what does winning this pay", "is a tie worth anything" — and until now the
 * answer was three taps away on another tab, which on a tee means nobody looks
 * it up and somebody guesses instead.
 *
 * It renders the same component the rules page does, from the same live config,
 * so the two cannot drift into disagreeing.
 */
export function RoundRules({
  doc,
  match,
  onClose,
}: {
  doc: Tournament
  match: Tournament['matches'][number]
  onClose: () => void
}) {
  /*
   * Bring it into view, because otherwise the tap reads as a dead button.
   *
   * The panel opens directly beneath the "i", and the "i" lives in the picker
   * row well down the screen — so on a phone the answer unfolds below the fold
   * and nothing changes at the point the thumb touched. Reported as "the i
   * button does nothing"; it was doing its whole job somewhere the man could
   * not see. `nearest` rather than `start` so a panel already on screen does
   * not jump under him for no reason.
   */
  const panel = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    panel.current?.scrollIntoView({ block: 'nearest' })
  }, [])

  return (
    <div ref={panel} className="border-b border-rule bg-wash">
      <div className="flex items-baseline justify-between px-4 pt-3">
        <span className="bc-label">{matchNameIn(doc, match)}</span>
        <button type="button" onClick={onClose} className="bc-tap bc-label text-signal px-2 -mr-2">
          Close
        </button>
      </div>
      <div className="px-4 pb-3">
        <MatchRulesFor doc={doc} match={match} />
      </div>
    </div>
  )
}

/**
 * Hole-at-a-time while you play, whole-round grid at the clubhouse.
 *
 * Two genuinely different jobs: big targets for one foursome on the course, and
 * fast bulk entry for four paper cards afterwards. One screen doing both would
 * be worse at each.
 */
/**
 * "one pop" through "nine pops", digits beyond that.
 *
 * Spoken words because the note reads as a sentence under the match line, not
 * as a figure in a column — and nobody has ever received ten pops on nine
 * holes, but if the draw produces it the digits are ready.
 */
const POP_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
const popCount = (n: number): string => `${POP_WORDS[n] ?? n} pop${n === 1 ? '' : 's'}`

function ModeToggle({ mode, onMode }: { mode: 'hole' | 'grid'; onMode: (m: 'hole' | 'grid') => void }) {
  return (
    <div className="grid grid-cols-2 border-b border-rule">
      {(['hole', 'grid'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onMode(m)}
          className={`bc-label py-2.5 bc-tap ${
            mode === m ? 'bg-ink text-paper' : 'bg-paper text-mute'
          }`}
        >
          {m === 'hole' ? 'Hole by hole' : 'Whole round'}
        </button>
      ))}
    </div>
  )
}

/**
 * A hole the group walked past without writing anything down.
 *
 * Worth interrupting for, because a gap is nearly free to fix on the next tee
 * and nearly impossible to fix that evening — nobody remembers what they made
 * on the 7th four hours later, and the hole quietly decides a match. It only
 * appears once something LATER has been entered, so a group standing on the
 * 7th is never told the 7th is missing.
 *
 * Each hole is a button, because being told what is wrong and then having to
 * find it yourself is the half of this that gets ignored.
 */
function MissingScores({
  doc,
  ctx,
  playerIds,
  holes,
  onGo,
}: {
  doc: Tournament
  ctx: ReturnType<typeof buildContext>
  playerIds: string[]
  holes: { number: number }[]
  onGo: (index: number) => void
}) {
  if (!ctx) return null
  const gaps = missingHoles(ctx, playerIds)
  if (gaps.length === 0) return null

  // Name whoever is actually missing, so nobody has to check four cards.
  const who = (holeNumber: number): string => {
    const i = holes.findIndex((h) => h.number === holeNumber)
    return playerIds
      .filter((pid) => ctx.scores[pid]?.[i] === undefined)
      .map((pid) => playerName(doc, pid))
      .join(', ')
  }

  return (
    <div className="bg-signal text-paper px-4 py-2.5">
      <p className="bc-label">
        {gaps.length === 1 ? 'A hole has no score' : `${gaps.length} holes have no score`}
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {gaps.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onGo(holes.findIndex((h) => h.number === n))}
            className="bc-tap bg-paper text-ink px-2.5 py-1 text-[13px] leading-tight text-left"
          >
            <span className="bc-num">{n}</span>
            <span className="text-mute"> · {who(n)}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Jump to any hole on the card.
 *
 * Walking forward one hole at a time is right for playing golf and wrong for
 * everything else — going back to fix the 4th, or picking up where a group left
 * off after somebody else did the scoring for two holes. The title is the
 * control because that is the thing on screen already naming the hole.
 *
 * Each square also says what is on the card there, so this doubles as the
 * answer to "what have we actually got": a filled square is scored, an outlined
 * one is not yet reached, and a red one is a hole that was walked past.
 */
function HolePicker({
  holes,
  current,
  scored,
  gaps,
  onPick,
}: {
  holes: { number: number }[]
  current: number
  /** Index -> every player in the group has a score there. */
  scored: boolean[]
  gaps: number[]
  onPick: (index: number) => void
}) {
  return (
    <div className="px-3 py-3 border-b border-rule bg-wash">
      {/*
        Five across, not nine.

        Nine tracks at this width are 35.7px each, and `.bc-tap` forces a 44px
        minimum on every square — tracks do not grow for min-width, so each
        button overflowed its cell and overlapped its neighbor, the last one
        eating the container's padding. Two rows of five give every square its
        full target and its own space.
      */}
      <div className="grid grid-cols-5 gap-1.5">
        {holes.map((h, i) => {
          const isGap = gaps.includes(h.number)
          const isNow = i === current
          return (
            <button
              key={h.number}
              type="button"
              onClick={() => onPick(i)}
              aria-label={`Hole ${h.number}${isGap ? ', missing a score' : ''}`}
              aria-current={isNow ? 'true' : undefined}
              className={`bc-num text-sm h-11 bc-tap border-2 ${
                isNow
                  ? 'bg-ink text-paper border-ink'
                  : isGap
                    ? 'bg-signal text-paper border-signal'
                    : scored[i]
                      ? 'bg-paper text-ink border-ink'
                      : 'bg-paper text-mute border-rule'
              }`}
            >
              {h.number}
            </button>
          )
        })}
      </div>
      <p className="text-[12px] text-mute mt-2 leading-snug">
        Outlined holes have every score in. Plain ones have not been played yet.
        {gaps.length > 0 ? ' Red ones were walked past.' : ''}
      </p>
    </div>
  )
}

/**
 * Signing for the card.
 *
 * The point at which a running total becomes money. Until a group signs, its
 * match pays nothing and counts for nothing — which is the fix for a ledger
 * that used to award the championship purse with the field thru 12, and that
 * ranked an unfinished card as the best round on the property.
 *
 * A card with gaps cannot be signed, and the button says which holes are
 * missing rather than simply refusing. The whole reason the gap banner exists
 * is that a hole is cheap to remember on the next tee and impossible to
 * reconstruct that evening; this is the moment that matters.
 *
 * Anyone in the group may sign. The four who played it are the people who know
 * whether it is right, and on this trip the admin is out playing too.
 */
/**
 * The card you are about to put your name to.
 *
 * Signing settles the round — points and money are counted from signed cards
 * only — and it used to be a button and a sentence. You were asked to vouch for
 * thirty-six numbers while looking at none of them, having last seen hole 3 an
 * hour and a half ago.
 *
 * So the whole card is here: four men down, the holes across, every figure as
 * entered. An empty hole is not a blank — it is filled, marked, and tappable,
 * because the one thing this screen must never do is let a gap look like a
 * finished card. Tapping a gap goes straight to that hole.
 *
 * Deliberately not the Cards screen's grid. That one is a read-only record with
 * net, pops and outcome shading; this is a checklist with one question in it,
 * and putting the answer to that question next to par and pops would bury it.
 */
function SignOffCard({
  doc,
  ctx,
  playerIds,
  onGoToHole,
}: {
  doc: Tournament
  ctx: NonNullable<ReturnType<typeof buildContext>>
  playerIds: string[]
  onGoToHole: (index: number) => void
}) {
  /*
   * Names as initials — SC, PD — because the width they free is the difference
   * between this table fitting an iPhone and scrolling on one. On a nine-hole
   * card the initials get the whole thing on screen at once, which matters
   * here more than anywhere: this is the signing view, and a column of scores
   * hidden off the right edge is a column being vouched for unseen. Collisions
   * widen only as far as they must (two Godfreys, two Peruccas — see
   * `shortNames`), and the full name stays available on the row as a title.
   */
  const abbrev = shortNames(playerIds.map((pid) => doc.players.find((p) => p.id === pid) ?? { id: pid, displayName: playerName(doc, pid) }))
  return (
    <div className="overflow-x-auto -mx-4 px-4 mt-3">
      <table className="text-xs border-collapse min-w-max">
        <thead>
          <tr>
            <th className="bc-label text-mute text-left pr-2 pb-1 sticky left-0 bg-wash">Hole</th>
            {ctx.holes.map((h) => (
              <th key={h.number} className="bc-num text-mute px-0.5 pb-1 w-7 text-center">
                {h.number}
              </th>
            ))}
            {/*
              Pinned to the right, not appended.
              The card is eighteen columns wide and scrolls; a total at the far
              end would be the one figure you have to go looking for, and it is
              the figure you are actually signing for.
            */}
            <th className="bc-label text-mute pb-1 w-9 text-right sticky right-9 bg-wash pl-2">
              Tot
            </th>
            <th className="bc-label text-mute pb-1 w-9 text-right sticky right-0 bg-wash">Par</th>
          </tr>
        </thead>
        <tbody>
          {playerIds.map((pid) => {
            /*
             * Totalled over the holes that actually carry a gross, and to par
             * over those same holes.
             *
             * A picked-up hole has no number to add, so a card with one in it has
             * no full gross score — and quoting a total as though it did would be
             * the sort of quiet wrongness this whole block exists to prevent. The
             * count is shown alongside whenever it is not the full round, so a
             * partial figure is never mistaken for a finished one.
             */
            let total = 0
            let parSoFar = 0
            let counted = 0
            ctx.holes.forEach((h, i) => {
              const v = ctx.scores[pid]?.[i]
              if (v?.state === 'holed') {
                total += v.gross
                parSoFar += h.par
                counted += 1
              }
            })
            const partial = counted !== ctx.holes.length
            return (
            <tr key={pid}>
              <td
                className="bc-name pr-2 py-0.5 whitespace-nowrap sticky left-0 bg-wash"
                title={playerName(doc, pid)}
              >
                {abbrev.get(pid) ?? playerName(doc, pid)}
              </td>
              {ctx.holes.map((h, i) => {
                const v = ctx.scores[pid]?.[i]
                const gross = v?.state === 'holed' ? v.gross : null
                if (v && gross == null) {
                  // Picked up or conceded: a real answer, not a gap.
                  return (
                    <td key={h.number} className="bc-num text-center px-0.5 py-0.5 text-mute">
                      &ndash;
                    </td>
                  )
                }
                if (gross != null) {
                  return (
                    <td key={h.number} className="bc-num text-center px-0.5 py-0.5">
                      {gross}
                    </td>
                  )
                }
                return (
                  <td key={h.number} className="p-0">
                    <button
                      type="button"
                      onClick={() => onGoToHole(i)}
                      aria-label={`${playerName(doc, pid)} has no score on hole ${h.number} — go there`}
                      className="bc-tap w-full bg-signal text-paper bc-num text-center px-0.5 py-0.5"
                    >
                      ?
                    </button>
                  </td>
                )
              })}
              <td
                className="bc-num text-right py-0.5 pl-2 sticky right-9 bg-wash"
                title={partial ? `${counted} of ${ctx.holes.length} holes` : undefined}
              >
                {counted > 0 ? total : '\u2014'}
                {partial && counted > 0 && <span className="text-mute">*</span>}
              </td>
              <td className="bc-num text-right py-0.5 sticky right-0 bg-wash">
                {counted > 0 ? toParLabel(total - parSoFar) : '\u2014'}
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ConfirmCard({
  doc,
  ctx,
  group,
  roundLabel,
  atTheEnd,
  counts,
  called,
  onGoToHole,
}: {
  doc: Tournament
  ctx: ReturnType<typeof buildContext>
  group: { id: string; roundId: string; playerIds: string[]; teeTime: string }
  roundLabel: string
  /** Whether the scorer has reached the last hole of the round. */
  atTheEnd: boolean
  /** False for a round played for fun, which settles nothing and signs nothing. */
  counts: boolean
  /** The round was called on darkness — unplayed holes are not missing. */
  called: boolean
  onGoToHole: (index: number) => void
}) {
  const confirmations = useStore(mergedConfirmations)
  // Unlocking moves money that has already been counted, so it stays with
  // whoever holds the setup PIN. Signing does not — see the note above.
  const isAdmin = useStore((st) => st.session?.isAdmin ?? false)
  const signed = confirmations.find(
    (c) => c.roundId === group.roundId && c.groupId === group.id,
  )
  if (!ctx) return null

  /*
   * A round played for fun is never signed for.
   *
   * Signing is the act that turns a running total into money, and there is no
   * money here — the ledger, the seeding and the outstanding-cards nag all skip
   * this round already. Leaving the button on the screen would contradict all
   * three in the plainest possible way, since the sentence under it says
   * signing settles the round.
   */
  if (!counts) {
    return (
      <div className="px-4 py-3 border-b border-rule bg-wash">
        <p className="text-[13px] text-mute leading-snug">
          <b className="bc-label text-ink">Just for fun.</b> Nothing to sign — this round
          pays nothing and moves nothing. The card is yours to keep, and any side bet on it
          settles as usual.
        </p>
      </div>
    )
  }

  const gaps = missingHoles(ctx, group.playerIds)
  /*
   * A called round's blanks are not missing scores — the holes were never
   * played, and the horn is why. The card signs on what stands. Gaps BEHIND
   * the last entry still block, because a skipped 5th is a real hole of real
   * golf whether or not the 9th got dark.
   */
  const everyHoleIn =
    called ||
    group.playerIds.every((pid) =>
      ctx.holes.every((_, i) => ctx.scores[pid]?.[i] !== undefined),
    )

  const clearer = (
    <ClearCard roundId={group.roundId} groupId={group.id} roundLabel={roundLabel} teeTime={group.teeTime} />
  )

  if (signed) {
    /*
     * A signature is for the card that was in front of the man who gave it.
     *
     * Change the round's nine — or its course — afterwards and the card under
     * the signature is a different one: nine blank holes, every match all
     * square, and the money it settled quietly recomputed to nothing. The
     * screen went on saying "This card is counted in the standings" over an
     * empty grid, which is the most confident lie in the app.
     *
     * The signature is not thrown away. It says what happened, and an admin can
     * unlock, fix the round, and sign again.
     */
    const strandedBySomeEdit = gaps.length > 0 || !everyHoleIn
    return (
      <div className={`px-4 py-3 border-b border-rule ${strandedBySomeEdit ? 'bg-signal text-paper' : 'bg-wash'}`}>
        <p className="bc-label">{strandedBySomeEdit ? 'Signed, but this is not that card' : 'Card signed'}</p>
        {strandedBySomeEdit ? (
          <p className="text-[13px] leading-snug mt-1">
            {roundLabel} · {clockTime(group.teeTime)} was signed by{' '}
            <b className="bc-name">{playerName(doc, signed.confirmedBy)}</b>, and the round has
            been changed since — these holes have no scores on them. Nothing here is being
            counted correctly. An admin should unlock it, put the round back or re-enter the
            card, and sign again.
          </p>
        ) : (
        <p className="text-[13px] text-mute leading-snug mt-1">
          {roundLabel} · {clockTime(group.teeTime)} — signed by{' '}
          <b className="bc-name text-ink">{playerName(doc, signed.confirmedBy)}</b>. This
          card is counted in the standings.
          {isAdmin ? '' : ' An admin can unlock it if something is wrong.'}
        </p>
        )}
        {isAdmin && (
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={() => setCardConfirmed(group.roundId, group.id, false)}
              className={`bc-tap bc-label border-2 px-4 py-2 ${
                strandedBySomeEdit ? 'border-paper' : 'border-ink'
              }`}
            >
              Unlock this card
            </button>
          </div>
        )}
      </div>
    )
  }

  const blocked = gaps.length > 0 || !everyHoleIn

  /*
   * A disabled button saying "Card not finished" is not worth a screen.
   *
   * It sat above the scores for all nine holes: on the 2nd tee it announced
   * something nobody could act on and nobody doubted, and it pushed the four
   * steppers — the entire job of this screen — below the fold. So while the card
   * is genuinely unfinished it appears only once you are standing on the last
   * hole, which is the first moment it is either true news or a thing to do.
   *
   * The signable and signed states always show. Those are money.
   */
  if (blocked && !atTheEnd) return null

  return (
    <div className="px-4 py-3 border-b border-rule bg-wash">
      <button
        type="button"
        disabled={blocked}
        onClick={() => setCardConfirmed(group.roundId, group.id, true)}
        className={`bc-tap bc-label w-full py-4 ${
          blocked ? 'bg-wash text-mute border-2 border-rule' : 'bg-signal text-paper'
        }`}
      >
        {blocked ? 'Card not finished' : 'Sign for this card'}
      </button>
      <p className="text-[13px] text-mute leading-snug mt-2">
        {blocked
          ? 'Every red ? below needs a score. Tap one to go to that hole. A total marked * covers only the holes that have one.'
          : 'Signing settles this round — points and money are counted from signed cards only. Anyone in the group can sign. A total marked * covers only the holes with a gross score, since a picked-up hole has none.'}
      </p>
      <Disagreements
        doc={doc}
        ctx={ctx}
        roundId={group.roundId}
        playerIds={group.playerIds}
        onGoToHole={onGoToHole}
      />
      <SignOffCard doc={doc} ctx={ctx} playerIds={group.playerIds} onGoToHole={onGoToHole} />
      {isAdmin && clearer}
    </div>
  )
}

/**
 * Squares two men filled in differently, asked about before the card is signed.
 *
 * On the dry run three men wrote Poulton's 3rd inside a minute. The last one
 * won on the clock, a 7 landed on a hole he had birdied, the birdie alert that
 * had already gone to twelve phones was withdrawn, and no screen said anything
 * at all. The server had the whole story in `score_events` and nothing read it.
 *
 * This is the moment to ask, because it is the last one: after the signature
 * the money is counted and only an admin can reopen it. It is a question, not a
 * blocker — two men marking the same card is normal and the standing number is
 * usually right — so it names both, says who typed each and when, and lets
 * somebody settle it with one tap.
 *
 * Loaded when the sign-off appears rather than polled: the history is only
 * interesting at this one moment, and twelve phones asking for it every eight
 * seconds all week to show nothing would be a poor trade.
 */
export function Disagreements({
  doc,
  ctx,
  roundId,
  playerIds,
  onGoToHole,
}: {
  doc: Tournament
  ctx: RoundContext
  roundId: string
  playerIds: string[]
  onGoToHole: (holeNo: number) => void
}) {
  const [events, setEvents] = useState<ScoreEvent[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    setEvents(null)
    setFailed(false)
    call<{ events: ScoreEvent[] }>(`score-history?roundId=${encodeURIComponent(roundId)}`)
      .then((r) => {
        if (live) setEvents(r.events)
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [roundId])

  /*
   * Recomputed from the events, so choosing a value updates the list on the
   * next load rather than pretending. The filter to this foursome matters: the
   * endpoint answers for the whole round, and another group's argument is not
   * this signer's to settle.
   */
  const cells = useMemo(() => {
    if (!events) return []
    const mine = new Set(playerIds)
    return contestedCells(events.filter((e) => mine.has(e.playerId)))
  }, [events, playerIds])

  if (failed) {
    return (
      <p className="text-[13px] text-mute leading-snug mt-3">
        Could not check this card for double entries — no signal. The scores themselves are
        fine; this is only the check.
      </p>
    )
  }
  if (!events || cells.length === 0) return null

  /** "3:34pm", which is how a man remembers when a hole happened. */
  const at = (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime())
      ? ''
      : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  }

  const shows = (e: { state: string; gross: number | null }) =>
    e.state === 'holed' ? String(e.gross) : e.state === 'pickedUp' ? 'picked up' : 'conceded'

  return (
    <div className="mt-3 border-2 border-signal">
      <p className="bc-label bg-signal text-paper px-3 py-1.5">
        {cells.length === 1 ? 'One hole has two answers' : `${cells.length} holes have two answers`}
      </p>
      <ul className="divide-y divide-rule">
        {cells.map((cell) => {
          const hole = ctx.holes.find((h) => h.number === cell.holeNo)
          return (
            <li key={`${cell.playerId}-${cell.holeNo}`} className="px-3 py-2.5">
              <button
                type="button"
                onClick={() => onGoToHole(cell.holeNo)}
                className="bc-tap bc-label text-signal"
              >
                Hole {cell.holeNo} · {playerName(doc, cell.playerId)}
              </button>
              <div className="mt-1.5 flex flex-col gap-1.5">
                {cell.entries.map((e) => (
                  <button
                    key={`${e.enteredBy}-${e.enteredAt}`}
                    type="button"
                    disabled={e.standing}
                    onClick={() =>
                      setScore(roundId, cell.playerId, cell.holeNo, {
                        state: e.state,
                        ...(e.state === 'holed'
                          ? { gross: e.gross as number }
                          : e.state === 'pickedUp'
                            ? { mostLikely: e.gross }
                            : {}),
                      } as HoleScoreValue)
                    }
                    className={`bc-tap flex items-baseline gap-2 text-left px-2.5 py-2 border-2 ${
                      e.standing ? 'border-ink bg-wash' : 'border-rule'
                    }`}
                  >
                    <span className="bc-num text-ink w-10 shrink-0">{shows(e)}</span>
                    <span className="text-[13px] text-mute">
                      {playerName(doc, e.enteredBy)} entered it{e.enteredAt ? `, ${at(e.enteredAt)}` : ''}
                      {e.standing ? ' — on the card now' : ''}
                    </span>
                  </button>
                ))}
              </div>
              {hole ? (
                <p className="text-[12px] text-dim mt-1">Par {hole.par}</p>
              ) : null}
            </li>
          )
        })}
      </ul>
      <p className="text-[13px] text-mute leading-snug px-3 py-2">
        Two men put different numbers on the same square. Tap the right one before you sign —
        after that only an admin can change it.
      </p>
    </div>
  )
}

/**
 * Wipe this foursome's card and start it again.
 *
 * The reset button is all-or-nothing and much too big for what people actually
 * need — a group scored a round against the wrong four men, or a rehearsal left
 * numbers on Tuesday. This clears one card, or the round it belongs to, and
 * nothing else.
 *
 * Two taps, admin only, and it says the count out loud afterwards: the one
 * thing worse than not being able to clear a card is not being sure whether you
 * just cleared the right one.
 */
function ClearCard({
  roundId,
  groupId,
  roundLabel,
  teeTime,
}: {
  roundId: string
  groupId: string
  roundLabel: string
  teeTime: string
}) {
  const [armed, setArmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<string | null>(null)

  const run = async (whole: boolean) => {
    setBusy(true)
    setSaid(null)
    try {
      const r = await clearScores(roundId, whole ? undefined : groupId)
      setSaid(
        whole
          ? `${roundLabel} cleared — ${r.groups} cards, ${r.players} players.`
          : `${clockTime(teeTime)} cleared. ${r.remainingInRound} score${r.remainingInRound === 1 ? '' : 's'} left in ${roundLabel}.`,
      )
    } catch (e) {
      setSaid(e instanceof Error ? e.message : 'Could not clear it.')
    } finally {
      setBusy(false)
      setArmed(false)
    }
  }

  if (said) {
    return (
      <div className="mt-3 pt-3 border-t border-rule">
        <p className="text-[13px] text-mute leading-snug">{said}</p>
      </div>
    )
  }

  return (
    <div className="mt-3 pt-3 border-t border-rule flex gap-2">
      <button
        type="button"
        disabled={busy}
        onClick={() => (armed ? void run(false) : setArmed(true))}
        className={`flex-1 bc-tap bc-label py-2.5 disabled:opacity-40 ${
          armed ? 'bg-signal text-paper' : 'border-2 border-rule text-mute'
        }`}
      >
        {busy ? 'Clearing…' : armed ? 'Tap again to clear' : 'Clear this card'}
      </button>
      {armed && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void run(true)}
          className="bc-tap bc-label py-2.5 px-3 border-2 border-ink disabled:opacity-40"
        >
          Whole round
        </button>
      )}
    </div>
  )
}

export function Score({ doc }: { doc: Tournament }) {
  const [mode, setMode] = useState<'hole' | 'grid'>('hole')
  /*
   * The live board is a sibling screen, reached from the strip below.
   *
   * Held here rather than in a tab because a side match belongs to the round in
   * front of you: you come back from it to the same hole on the same card, which
   * a tab change would not.
   */
  const [sideBoardId, setSideBoardId] = useState<string | null>(null)
  /* Adding a round is a sibling screen for the same reason the side board is:
     you come back from it onto the round you just created. */
  const [adding, setAdding] = useState(false)
  /* Not a sibling screen: the rules belong beside the card you are scoring,
     and leaving the hole you are on to read them is the reason nobody does. */
  const [rulesOpen, setRulesOpen] = useState(false)
  const scores = useStore(mergedScores)
  const misses = useStore((st) => st.misses)
  const me = useStore((s) => s.session?.playerId ?? '')

  const [roundId, setRoundId] = useState(() => {
    const recalled = recalledRound()
    if (recalled && doc.rounds.some((r) => r.id === recalled)) return recalled
    return currentRoundId(
      doc,
      (id) => {
        const r = doc.rounds.find((x) => x.id === id)
        const c = r ? courseOf(doc, r) : undefined
        const gs = doc.groups.filter((g) => g.roundId === id)
        if (!r || !c || gs.length === 0) return false
        const holes = holesFor(c, r.nine)
        return gs.every((g) =>
          g.playerIds.every((pid) =>
            holes.every((h) =>
              scores.some(
                (x) => x.roundId === id && x.playerId === pid && x.holeNo === h.number,
              ),
            ),
          ),
        )
      },
      todayLocal(),
    )
  })
  useEffect(() => {
    if (roundId) rememberRound(roundId)
  }, [roundId])
  const round = doc.rounds.find((r) => r.id === roundId) ?? doc.rounds[0]
  const course = round ? courseOf(doc, round) : undefined

  // Default to the group you are actually in — nobody wants to hunt for their
  // own foursome on the first tee.
  const groups = doc.groups.filter((g) => g.roundId === round?.id)
  const [groupId, setGroupId] = useState('')
  const group =
    groups.find((g) => g.id === groupId) ??
    groups.find((g) => g.playerIds.includes(me)) ??
    /*
     * Whoever is out first, when the man reading is in no foursome at all —
     * a thirteenth arrival, or somebody dropped from the draw. He gets a real
     * card to look at, and the strip below says whose it is, because the old
     * behavior handed him the 09:00 group with nothing to say he was not in
     * it.
     */
    [...groups].sort((a, b) => a.index - b.index)[0]

  const confirmations = useStore(mergedConfirmations)
  /*
   * Signed is a mode, not a footnote. Once this foursome's card is signed
   * the entry form BECOMES the printed card: controls leave, the numbers
   * set like type, and the ground takes the faint tone of a filed document.
   * Scott's ask, arrived at the long way — gray-out was the first idea, but
   * gray says broken, and a signed card is read all day.
   */
  const cardSigned = Boolean(
    group && confirmations.find((c) => c.roundId === round?.id && c.groupId === group.id),
  )
  const [hole, setHole] = useState(0)
  /*
   * The errand a card cell sent us on: this foursome, this hole. Taken once
   * at mount — the target names a group in the round the tapper ALSO set via
   * rememberRound, and the hole lands in the effect below because hole
   * numbers only become indexes once the round's holes are known.
   */
  const target = useRef(takeScoreTarget())
  useEffect(() => {
    if (target.current?.groupId && groups.some((g) => g.id === target.current!.groupId)) {
      setGroupId(target.current.groupId)
    }
    // Intentionally once: the target is an errand, not a subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [pickerOpen, setPickerOpen] = useState(false)
  const holes = useMemo(
    () => (course && round ? holesFor(course, round.nine) : []),
    [course, round],
  )
  useEffect(() => {
    const t = target.current
    if (!t) return
    const i = holes.findIndex((h) => h.number === t.holeNo)
    if (i >= 0) {
      setHole(i)
      target.current = null
    }
  }, [holes])

  const current = holes[hole]

  const match = doc.matches.find((m) => m.roundId === round?.id)
  const ctx = useMemo(
    () => (round && match ? buildContext(doc, round, match, scores) : null),
    [doc, round, match, scores],
  )

  // Where this foursome's match stands, right now. Rendered from the format's
  // own StatusChip and rankValue, so a 1v1, a 2v2 and a four-way quota all come
  // out of the same three lines and adding a seventh format changes nothing.
  const standing = useMemo(() => {
    if (!ctx || !match || !group) return []
    const { tabs } = runMatch(ctx, match, {
      existing: doc.tiebreaks,
      seed: doc.settings.rngSeed,
    })
    /*
     * Whose race is it. A group quota and every head-to-head are settled
     * inside the cart, so the cart is the list; a FIELD quota is settled
     * against all twelve, and showing three of them was answering a
     * question nobody asked — Scott, on the Wednesday nines.
     */
    const fieldWide =
      match.config.kind === 'quota' && match.config.rankingScope === 'field'
    return tabs
      .filter((t) => fieldWide || t.groupId === group.id)
      .sort((a, b) => b.rankValue - a.rankValue)
  }, [ctx, match, group, doc.tiebreaks, doc.settings.rngSeed])

  /*
   * The card in contest order, and the contests themselves.
   *
   * The draw stores a foursome as it was drawn; the golf inside it is two
   * contests, and until now nothing on this screen said which two. A scorer
   * walking up the 4th could not tell from his own card whether the man beside
   * him was his opponent or the other match's — the four rows were four
   * strangers in draw order with one truncated grey line underneath.
   *
   * Filtered to THIS round's match. Two matches on one round would otherwise
   * fold both sets of contests into one card and sit a man next to somebody he
   * is not playing, which is the exact error this is here to fix.
   */
  const card = useMemo(() => {
    if (!group) return { playerIds: [] as string[], contestStarts: new Set<number>(), sideStarts: new Set<number>(), contests: [] }
    return contestOrder(
      group.playerIds,
      doc.pairings.filter((p) => p.groupId === group.id && (!match || p.matchId === match.id)),
    )
  }, [group, doc.pairings, match])

  /*
   * The tab comes back where the scorer left it — or at the first open frame.
   *
   * Score unmounts on every tab switch, so `hole` reset to 1 and a man who
   * checked the odds on the 14th tee re-entered on the 1st, fourteen taps
   * from his golf. The round already had a memory (rememberRound); the hole
   * now shares it, per round so yesterday's card cannot misplace today's.
   * With nothing remembered, the first hole anybody still owes a score —
   * a scorer opens the app to enter, and the open frame is where entry is.
   * The deep-link errand outranks both: a tapped cell means THAT hole.
   */
  const seededFor = useRef<string | null>(null)
  useEffect(() => {
    if (!round || holes.length === 0 || !ctx) return
    if (seededFor.current === round.id) return
    seededFor.current = round.id
    if (target.current) return
    const remembered = recalled(`hole:${round.id}`)
    if (remembered != null) {
      const i = Number(remembered)
      if (Number.isInteger(i) && i >= 0 && i < holes.length) {
        setHole(i)
        return
      }
    }
    const openFrame = holes.findIndex((_, i) =>
      card.playerIds.some((pid) => ctx.scores[pid]?.[i] === undefined),
    )
    if (openFrame > 0) setHole(openFrame)
  }, [round, holes, ctx, card])

  useEffect(() => {
    // Only once this round has seeded — the moment of switching rounds fires
    // this with the OLD hole against the NEW round's key.
    if (round && seededFor.current === round.id) {
      remember(`hole:${round.id}`, String(hole))
    }
  }, [hole, round])

  /*
   * Where each man's MATCH stands, pinned to his name — Scott's ask from the
   * course: the score of the match front and center, an arrow and a number,
   * and the side that is up wearing a quiet band. Derived per player from
   * his side's tab against the opposing side's in the same contest; AS wears
   * nothing, because all square is the absence of news.
   */
  const sideStatus = useMemo(() => {
    const out = new Map<string, { diff: number; label: string; sub?: string }>()
    /*
     * A quota round has no head-to-heads — every man is playing the field or
     * his own foursome — so there is no "up" to print. What he wants beside
     * his name is where he STANDS: his running total against the number
     * everybody is chasing. Scott's ask, walking Old Macdonald.
     */
    if (match?.config.kind === 'quota') {
      for (const t of standing) {
        const pid = t.playerIds[0]
        if (!pid) continue
        const d = t.detail
        if (d?.kind !== 'quota') continue
        /*
         * The sum said out loud: the points his handicap starts him with,
         * plus what he has earned on the course. Scott's wording exactly —
         * no target arithmetic beside the name, because the number every man
         * is chasing is the same for everybody and printing his distance
         * from it invites a comparison the round already makes.
         *
         * `banked` is the head start, derived from the target the same way
         * the engine derives it, so an explicit committee quota stays honest.
         */
        // Straight off the chip, so the scorer's row and the board cannot
        // drift into quoting two different sums for one man.
        out.set(pid, { diff: 0, label: t.chip.primary, sub: t.chip.secondary })
      }
      return out
    }
    for (const c of card.contests ?? []) {
      const [sa, sb] = c.pairing.sides
      const ta = standing.find((t) => t.unitId === sa!.id)
      const tb = standing.find((t) => t.unitId === sb!.id)
      if (!ta || !tb) continue
      const read = (primary: string): { diff: number; label: string } => {
        const up = primary.match(/^(\d+)\s*UP$/i)
        if (up) return { diff: 1, label: up[1]! }
        const dn = primary.match(/^(\d+)\s*DN$/i)
        if (dn) return { diff: -1, label: dn[1]! }
        // High-low tabs score in points-for–points-against ("5 - 3").
        const hl = primary.match(/^(\d+)\s*-\s*(\d+)$/)
        if (hl) {
          const [a, b] = [Number(hl[1]), Number(hl[2])]
          return { diff: Math.sign(a - b), label: a === b ? '' : `${a}-${b}` }
        }
        if (/^WON/i.test(primary)) return { diff: 1, label: primary.replace(/^WON\s*/i, '') }
        if (/^LOST/i.test(primary)) return { diff: -1, label: primary.replace(/^LOST\s*/i, '') }
        return { diff: 0, label: '' }
      }
      for (const pid of sa!.playerIds) out.set(pid, read(ta.chip.primary))
      for (const pid of sb!.playerIds) out.set(pid, read(tb.chip.primary))
    }
    return out
  }, [card, standing])

  /*
   * Every contest in this foursome, each with the standing of both its sides.
   *
   * Yours first. `standing` is sorted by who is winning, which is the right
   * order for a leaderboard and the wrong one for this: the line you came to
   * read would move up and down the block as the match turned.
   */
  const contestLines = useMemo(() => {
    if (!group) return []
    return card.contests
      .map((c) => ({
        id: c.pairing.id,
        mine: c.playerIds.includes(me),
        sides: c.pairing.sides.map((sd) => ({
          names: sd.playerIds.map((x) => playerName(doc, x)).join(' & '),
          mine: sd.playerIds.includes(me),
          tab: standing.find((t) => t.unitId === sd.id),
        })),
      }))
      .sort((a, b) => Number(b.mine) - Number(a.mine))
  }, [card.contests, standing, doc, me, group])

  /*
   * Disarm a held pick-up whenever the target moves.
   *
   * THIS MUST STAY ABOVE THE RETURNS BELOW, and it did not: it lived after the
   * `!round || !course || !group` guard, because it wants those to be non-null.
   * So tapping "Whole round" returned `<Grid>` at the check below and rendered
   * one hook FEWER than the render before it, which is React error #300 — the
   * whole Score tab replaced by "Something broke", on the tab that exists to
   * enter scores. Reported from a phone; reproduced by mounting Score against
   * both live tournaments.
   *
   * Optional-chained instead, which costs nothing: with no round or group there
   * is no armed pick-up to clear, and `undefined` is a stable dependency.
   */

  // After every hook, so switching modes never changes the hook order.
  if (mode === 'grid') {
    return <Grid doc={doc} header={<ModeToggle mode={mode} onMode={setMode} />} />
  }

  if (sideBoardId) {
    return <SideBoard doc={doc} matchId={sideBoardId} onBack={() => setSideBoardId(null)} />
  }

  if (adding) {
    return (
      <AddRound
        doc={doc}
        me={me}
        onCancel={() => setAdding(false)}
        onDone={(id) => {
          // Land on the round that was just created, on its first hole. Coming
          // back to whatever was open before would be a strange answer to
          // having just built somewhere to score.
          setAdding(false)
          setRoundId(id)
          setHole(0)
        }}
      />
    )
  }

  /*
   * A round with no draw is not a dead end.
   *
   * This used to return before the round picker rendered, so the gap between
   * "rounds added" and "pairings generated" — and any round whose draw was
   * cleared — showed "No rounds set up yet" with no control to reach the eight
   * rounds that were perfectly fine. The picker comes first now, and the
   * message says which of the two things is actually missing.
   */
  if (!round || !course || !group || !current) {
    return (
      <>
        <Bar title="Score" kicker={round?.label} />
        <ModeToggle mode={mode} onMode={setMode} />
        {doc.rounds.length > 0 && (
          <div className="flex gap-2 px-4 py-3 border-b border-rule">
            <select
              value={round?.id ?? ''}
              onChange={(e) => {
                setRoundId(e.target.value)
                setHole(0)
              }}
              className="bc-label border border-ink px-2 py-2 bc-tap bg-paper"
              aria-label="Round"
            >
              {inPlayOrder(doc.rounds)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {roundPickerLabel(doc, r)}
                  </option>
                ))}
            </select>
          </div>
        )}
        <Empty>
          {doc.rounds.length === 0
            ? 'No rounds set up yet. An admin can add them under Setup.'
            : !course
              ? 'This round has no course yet. An admin can set one under Setup.'
              : 'No draw for this round yet — an admin can generate the pairings under Setup. Other rounds may already be ready.'}
        </Empty>
        {/* The dead end has a way out: whatever the schedule says, a group
            standing on a tee can build a round for itself. */}
        <div className="px-4 pb-6">
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="bc-tap w-full border-2 border-ink bc-label py-3"
          >
            Add a round
          </button>
        </div>
      </>
    )
  }

  /*
   * Par, yardage, stroke index — the three things on a tee marker.
   *
   * The yardage is YOUR tee's, named, because a foursome can be split across tees
   * and an unlabelled number would be somebody else's. It is omitted rather than
   * guessed when the card has no cell for that tee, which is the honest failure:
   * an invented yardage is a lie on a scorecard, and this one would be read
   * standing on the tee deciding what to hit.
   *
   * Stroke index comes last now. It is the field that decides every pop and it
   * belongs on the card rather than in the one line somebody glances at between
   * shots, where par and distance are what is actually wanted.
   */
  const viewer = doc.players.find((p) => p.id === me)
  // Falls back to the longest tee only for a viewer who is not on the roster —
  // an admin's phone signed in as somebody with no card of his own.
  const myTee = viewer ? findTee(course, viewer) : course.tees[0]
  const myYards = myTee ? current.yards[myTee.id] : undefined
  /*
   * No tee name. Everybody plays the Green all week, so it said nothing —
   * and it was the word that pushed the line toward wrapping. The yardage
   * is already YOUR tee's yardage; whose card it came off is on the round.
   */
  const holeLine = [
    `Par ${current.par}`,
    typeof myYards === 'number' ? `${myYards} yds` : null,
    `SI ${current.strokeIndex}`,
  ]
    .filter(Boolean)
    .join(' · ')

  const scoreOf = (pid: string): HoleScoreValue | undefined =>
    scores.find(
      (s) => s.roundId === round.id && s.playerId === pid && s.holeNo === current.number,
    )?.value

  const bump = (pid: string, delta: number) => {
    const v = scoreOf(pid)
    /*
     * The first tap on an empty cell writes par, whichever tap it is.
     *
     * `+` already did: base was par and it added one, so a stray tap on a man
     * nobody had scored yet put a bogey on the card, which is visible and
     * obviously wrong. `−` did the same arithmetic in the other direction and
     * wrote par MINUS one — a birdie. That is not visible as an error at all: it
     * is a plausible number, it counts on the tiebreak chain, and thirty seconds
     * later it tells twelve phones about a birdie nobody made. A push cannot be
     * recalled.
     *
     * So an empty cell answers its first tap with par either way, and the second
     * tap is the one that moves it.
     */
    if (v?.state !== 'holed') {
      setScore(round.id, pid, current.number, { state: 'holed', gross: current.par })
      return
    }
    const next = Math.max(1, Math.min(15, v.gross + delta))
    setScore(round.id, pid, current.number, { state: 'holed', gross: next })
  }

  return (
    <>
      {/*
        The masthead comes first on every tab.

        Score and Cards used to put their toggle above it while Board and Odds
        put theirs below, so the crest — and the title under it — jumped 44px
        down the screen every time you moved between tabs. Nothing else in the
        app moves like that, and it read as the page reloading.
      */}
      {/*
        The hole number is the FIGURE, huge, on the right — not the word.
        Scott, mid-round: tapping Next hole barely changed the header, so his
        eye never got told. The title keeps a small "Hole n" for the picker's
        caret to hang from, but the number a walking scorer checks is the
        56px one, and both are the same tap.
      */}
      <Bar
        kicker={round.label}
        title={`Hole ${current.number}`}
        figure={String(current.number)}
        sub={holeLine}
        subLg
        live
        onTitle={() => setPickerOpen((v) => !v)}
      />
      <ModeToggle mode={mode} onMode={setMode} />
      {pickerOpen && (
        <HolePicker
          holes={holes}
          current={hole}
          scored={holes.map((_, i) =>
            group.playerIds.every((pid) => ctx?.scores[pid]?.[i] !== undefined),
          )}
          gaps={ctx ? missingHoles(ctx, group.playerIds) : []}
          onPick={(i) => {
            setHole(i)
            setPickerOpen(false)
          }}
        />
      )}
      {!course.verified && <UnverifiedBanner courseName={course.name} />}




      {/*
        The four steppers come first, because they are the job.

        This screen used to open with a disabled "Card not finished" button, a
        missing-score notice, two dropdowns, the side matches and the match
        standing — six blocks — and only then the four numbers somebody is
        standing on a tee to enter. On a 393px phone the job was below the fold
        and everything above it was context for a task you could not yet see.
        Now: where you are, what you are entering, and what it did to the match.
        Everything you set once, or read at the end, sits underneath.
      */}
      {/*
        No hairline between the men — Scott's call: the gray dividers said
        nothing and drowned the one boundary that matters. The boundary
        BETWEEN two contests is a single thin line in the pop blue below, so
        the matches separate at a glance without a ruled ledger between every
        name.
      */}
      {cardSigned && (
        <div className="bg-ink text-paper px-4 py-2 flex items-center justify-between gap-3">
          <span className="bc-label">Card signed — locked</span>
          {(() => {
            /*
             * The turn, one tap. A signed nine's next question is "where do
             * I score the back?" — especially the same course two hours
             * later. The next round in play order, group re-defaulted to
             * your own foursome there.
             */
            const ordered = inPlayOrder(doc.rounds)
            const next = ordered[ordered.findIndex((r) => r.id === round.id) + 1]
            if (!next) return null
            return (
              <button
                type="button"
                onClick={() => {
                  setRoundId(next.id)
                  setGroupId('')
                  setHole(0)
                }}
                className="bc-tap bc-label border-2 border-paper px-3 py-1.5 whitespace-nowrap"
              >
                Next nine ›
              </button>
            )
          })()}
        </div>
      )}
      <ul className={`border-t border-rule ${cardSigned ? 'bc-sealed' : ''}`}>
        {card.playerIds.map((pid, rowIndex) => {
          const v = scoreOf(pid)
          /*
             Where the golf changes — and the two kinds of change look nothing
             alike, deliberately. A side break is the "v" inside the one match
             these rows belong to, and it earns a marked line. A contest break
             separates two matches that have nothing to do with each other, and
             it used to get the same v — so a card of two singles read as a
             chain of "A v B v C v D", which places a v between two men who are
             not playing each other. Between strangers, silence: a plain gap.
          */
          const breakKind = card.contestStarts.has(rowIndex)
            ? 'contest'
            : card.sideStarts.has(rowIndex)
              ? 'side'
              : null
          const pops = ctx?.pops[pid]?.[hole] ?? 0
          const pending = isPending(round.id, pid, current.number)
          const missed = misses.some(
            (x) => x.roundId === round.id && x.playerId === pid && x.holeNo === current.number,
          )
          const gross = v?.state === 'holed' ? v.gross : null
          const net = ctx ? roundNet(ctx, pid) : null
          return (
            <Fragment key={pid}>
              {breakKind === 'contest' && (
                <li data-contest-break="contest" aria-hidden="true" className="px-4 py-1.5">
                  <span className="block h-[2px] bg-pop" />
                </li>
              )}
              {breakKind === 'side' && (
                <li
                  data-contest-break="side"
                  aria-hidden="true"
                  className="flex items-center gap-2 px-4 py-1"
                >
                  <span className="h-px flex-1 bg-rule" />
                  <span className="bc-label text-mute">v</span>
                  <span className="h-px flex-1 bg-rule" />
                </li>
              )}
            <li
              className={`px-4 py-1.5 flex flex-col ${
                (sideStatus.get(pid)?.diff ?? 0) > 0 ? 'bg-signal/20' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-3 w-full">
              <div className="min-w-0">
                <div className="bc-name text-sm flex items-center gap-1.5">
                  <span className="truncate">{playerName(doc, pid)}</span>
                  {(() => {
                    const st = sideStatus.get(pid)
                    /*
                     * All square is the absence of news in match play, so a
                     * neutral marker stays silent there. A quota figure is
                     * not a margin — it is his score, and it prints whether
                     * or not he is ahead of anybody. (Shipped the other way
                     * for one deploy, which hid every man's quota at once.)
                     */
                    if (!st || (st.diff === 0 && !st.sub)) return null
                    /* Sized to keep company with the hole score itself —
                       Scott's calibration: the standing is why the hole
                       matters, so it prints at nearly the same weight —
                       and centered on it, arrow and number on one axis. */
                    return (
                      <span
                        className={`bc-num text-xl leading-none inline-flex items-baseline gap-0.5 ${
                          st.diff > 0 ? 'text-signal' : st.diff < 0 ? 'text-mute' : 'text-ink'
                        }`}
                      >
                        {st.sub ? (
                          <>
                            {/* Quota: the running total, with the sum that
                                makes it — start plus what he has earned. */}
                            {st.label}
                            <span className="text-[12px] font-normal text-mute ml-1">
                              {st.sub}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-[14px]">
                              {st.diff > 0 ? '\u25b2' : '\u25bc'}
                            </span>
                            {st.label}
                          </>
                        )}
                      </span>
                    )
                  })()}
                  {pops > 0 && (
                    <span className="bg-signal text-paper text-[11px] font-extrabold px-1 py-0.5">
                      {pops > 1 ? `${pops} POPS` : 'POP'}
                    </span>
                  )}
                  {pending && (
                    <span
                      className="w-1.5 h-1.5 rounded-full border border-mute"
                      title="Not yet synced"
                    />
                  )}
                </div>
                {/*
                  No verbs here any more — Scott's ruling from the course, in
                  two steps: "Wrong score?" moved to the whole-round grid
                  (corrections are found reading a card, not standing on a
                  tee), and then "Pick up" left too — the group holes
                  everything out, so the button was a stranger. The row is a
                  name and a stepper now, which is also the vertical room the
                  match standing below was asking for.
                */}
              </div>
              <div className="flex items-center gap-2">
                {/* The round so far, sitting between the name and the hole in
                    front of you — the two numbers a player actually wants. */}
                {/* w-14: "THRU 12" is ~51px at 11px uppercase with wide tracking and was
                    wrapping to two lines inside a 44px box, growing that one row
                    while its neighbors stayed short. */}
                <div className="text-right w-14 shrink-0">
                  <div
                    className={`bc-num text-base leading-none ${
                      net == null ? 'text-mute' : net.toPar < 0 ? 'text-signal' : ''
                    }`}
                  >
                    {net == null ? '–' : toParLabel(net.toPar)}
                  </div>
                  <div className="text-[11px] text-mute font-semibold uppercase tracking-wider mt-0.5">
                    {net == null ? 'net' : `thru ${net.thru}`}
                  </div>
                </div>
                {cardSigned ? (
                  <span className="bc-num text-2xl w-10 text-center">
                    {v?.state === 'pickedUp' ? '—' : (gross ?? '·')}
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => bump(pid, -1)}
                      className="bc-tap bg-ink text-paper w-11 h-11 text-xl font-semibold"
                      aria-label={`One fewer for ${playerName(doc, pid)}`}
                    >
                      −
                    </button>
                    <span className="bc-num text-2xl w-10 text-center">
                      {v?.state === 'pickedUp' ? '—' : (gross ?? '·')}
                    </span>
                    <button
                      type="button"
                      onClick={() => bump(pid, gross == null ? 0 : 1)}
                      className="bc-tap bg-ink text-paper w-11 h-11 text-xl font-semibold"
                      aria-label={`One more for ${playerName(doc, pid)}`}
                    >
                      +
                    </button>
                  </>
                )}
              </div>
              </div>
              {/*
                Under the score, and shaped like a button — Scott twice: it
                read as a caption, so it did not look tappable, and the tap
                had nothing to show for itself until the server answered.
                Both fixed: a bordered control that fills with signal when
                it is on, and a store that flips it the instant it is
                pressed. A miss is still not a score, so it lives below the
                number rather than among the figures that settle money.
              */}
              {!cardSigned && (
                <div className="flex justify-end w-full mt-1">
                  <button
                    type="button"
                    onClick={() => void markMiss(round.id, pid, current.number, !missed)}
                    aria-pressed={missed}
                    className={`bc-tap bc-label text-[11px] px-2.5 py-1 border-2 whitespace-nowrap ${
                      missed
                        ? 'bg-signal text-paper border-signal'
                        : 'border-rule text-mute bg-paper'
                    }`}
                  >
                    {missed ? 'Missed inside 3 ft \u2713' : 'Missed inside 3 ft?'}
                  </button>
                </div>
              )}
            </li>
            </Fragment>
          )
        })}
      </ul>

      {/*
        Previous and Next sit directly under the four entries.

        They were at the very foot of the screen, below the match standing, the
        dropdowns and the card — so the loop a scorer actually walks (four
        numbers, next hole, four numbers) needed a scroll past everything else on
        every single hole. Eighteen times a round, twice on a 27-hole day.
      */}
      <div className="flex gap-2 p-4">
        <button
          type="button"
          disabled={hole === 0}
          onClick={() => setHole((h) => h - 1)}
          className="bc-tap flex-1 border-2 border-ink bc-label py-3 disabled:opacity-30"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={hole >= holes.length - 1}
          onClick={() => setHole((h) => h + 1)}
          className="bc-tap flex-1 bg-ink text-paper bc-label py-3 disabled:opacity-30"
        >
          Next hole
        </button>
      </div>

      {/*
        One line, not four rows.

        This was a heading and a Row per unit — the same four names you had just
        finished scoring, repeated with a chip each, and about two hundred pixels
        of the only screen anybody uses on the course. It pushed the side matches
        and the hole navigation off the bottom.

        Your own standing gets the figure, because that is the one you came to
        read; the rest of the foursome trails behind it in small type. Everything
        that was here is still on the Board, laid out properly.
      */}
      {/*
        Say whose card this is, when it is not yours.
        
        A man in no foursome — a thirteenth arrival, somebody dropped from the
        draw — was shown the first group's card with nothing anywhere to say he
        was not in it, four names and all square, as if he were playing.
      */}
      {!group.playerIds.includes(me) && me !== '' && (
        <p className="px-4 py-2 text-[13px] leading-snug bg-signal text-paper">
          You are not in this foursome — you are not in any of this round&rsquo;s groups.
          This is {playerName(doc, group.playerIds[0] ?? '')}&rsquo;s card, and anyone may
          score it.
        </p>
      )}

      {/*
        One line per contest, both sides named, directly under the steppers.

        This was a single row: everybody else's standing crushed onto one
        truncated 12px line, with your own figure alone on the right. On a
        foursome playing two singles that line read "Bo 1 UP · Cy 1 DN" — two
        men who are playing EACH OTHER, presented as two unrelated facts, while
        the one thing a scorer wants ("am I up, and on whom") was split across
        the two ends of the row.

        Now each match gets its own line with a v in the middle and its margin
        on the right, at the size of the rest of the screen rather than the size
        of a footnote. The order is yours first — `standing` sorts by who is
        winning, which would walk your own line up and down the block as the
        match turned.

        The back nine is a different draw, and nothing here remembers: the
        contests are read off `doc.pairings` for the round in front of you.
      */}
      {contestLines.length > 0 ? (
        <div className="border-b border-rule px-4 py-2.5">
          <span className="bc-label text-mute">The match</span>
          <ul className="mt-1.5 space-y-1.5">
            {contestLines.map((c) => (
              <li
                key={c.id}
                data-contest-line={c.mine ? 'mine' : 'other'}
                className={`flex items-baseline justify-between gap-3 ${
                  c.mine ? '' : 'text-mute'
                }`}
              >
                <span className="min-w-0 text-sm leading-snug">
                  {c.sides.map((sd, i) => (
                    <Fragment key={i}>
                      {i > 0 && <span className="text-mute px-1.5">v</span>}
                      <span className={sd.mine ? 'bc-name' : ''}>{sd.names}</span>
                    </Fragment>
                  ))}
                  {/*
                    Who still has a shot coming, in the pop's own color — the
                    same ink as the POP chip on the card, so the two facts read
                    as one system. Derived from the holes the match has not
                    settled, and silent once it has: see `popsRemaining`.
                  */}
                  {(() => {
                    const owed = c.sides.flatMap((sd) =>
                      sd.tab
                        ? sd.tab.playerIds
                            .map((pid) => ({ pid, n: popsRemaining(sd.tab!, pid) }))
                            .filter((x) => x.n > 0)
                        : [],
                    )
                    if (owed.length === 0) return null
                    return (
                      <span className="block text-[12px] font-semibold text-pop mt-0.5">
                        {owed
                          .map((x) => `${playerName(doc, x.pid)} — ${popCount(x.n)} remaining`)
                          .join(' · ')}
                      </span>
                    )
                  })()}
                </span>
                {/*
                  One figure for the contest, not one per side: "2 UP" and
                  "2 DN" are the same fact said twice, and printing both invited
                  the reader to add them up. The leading side's chip is the
                  match's chip; a halved match reads the same from either end.
                */}
                {(() => {
                  const lead =
                    [...c.sides]
                      .filter((sd) => sd.tab)
                      .sort((a, b) => (b.tab!.rankValue ?? 0) - (a.tab!.rankValue ?? 0))[0] ?? null
                  if (!lead?.tab) return null
                  return (
                    <span className="text-right shrink-0 whitespace-nowrap">
                      <span className="bc-num text-sm leading-none">{lead.tab.chip.primary}</span>
                      <span className="text-[12px] text-mute block mt-0.5">
                        {lead.tab.chip.secondary}
                      </span>
                    </span>
                  )
                })()}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        /*
          Quota and the championship have no contests to line up — everybody
          plays the field. The old single row is exactly right for that, and it
          is the only thing left using it.
        */
        standing.length > 0 &&
        (match?.config.kind === 'quota' && match.config.rankingScope === 'field' ? (
          /*
            The whole field, ranked, in two columns — because on these nines
            every man is playing all eleven others and the only question is
            where he stands among them. Sorted by points, your own row lit,
            place numbers down the left so the cut is countable at a glance.
          */
          <div className="px-4 py-2.5 border-b border-rule">
            <span className="bc-label text-mute">Against the field</span>
            {/*
              Down the first column, then across — Scott's ruling, and how a
              ranked list has always been read on paper. A plain two-column
              grid fills left-to-right, which interleaves the order: 1 and 2
              side by side, 3 and 4 beneath. `grid-flow-col` with a fixed row
              count puts 1–6 down the left and 7–12 down the right.
            */}
            <ul
              className="mt-1.5 grid grid-flow-col gap-x-5 gap-y-1"
              style={{ gridTemplateRows: `repeat(${Math.ceil(standing.length / 2)}, auto)` }}
            >
              {standing.map((t, i) => {
                const pid = t.playerIds[0] ?? ''
                const mine = pid === me
                return (
                  <li
                    key={t.unitId}
                    className={`flex items-center gap-2 text-sm ${mine ? '' : 'text-mute'}`}
                  >
                    {/*
                      The place wears a box; the points do not. Both are
                      numbers sitting a thumb apart, and on the course Scott
                      read a man's rank as his score — so the rank stops
                      looking like a score at all: smaller, boxed, on wash.
                    */}
                    <span
                      className={`bc-num text-[11px] w-5 h-5 shrink-0 flex items-center justify-center ${
                        mine ? 'bg-ink text-paper' : 'bg-wash text-mute'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className={`min-w-0 truncate ${mine ? 'bc-name' : ''}`}>
                      {playerName(doc, pid)}
                    </span>
                    <span
                      className={`bc-num text-base ml-auto shrink-0 ${mine ? 'text-ink' : ''}`}
                    >
                      {t.chip.primary}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : (
          <div className="px-4 py-2.5 border-b border-rule flex items-baseline justify-between gap-3">
            <span className="min-w-0">
              <span className="bc-label text-mute">The match</span>
              <span className="text-[12px] text-mute block truncate mt-0.5">
                {standing
                  .filter((t) => !t.playerIds.includes(me))
                  .map((t) => `${t.playerIds.map((x) => playerName(doc, x)).join(' & ')} ${t.chip.primary}`)
                  .join(' · ') || 'Nobody else in this group'}
              </span>
            </span>
            {(() => {
              const mine = standing.find((t) => t.playerIds.includes(me))
              if (!mine) return null
              return (
                <span className="text-right shrink-0">
                  <span className="bc-num text-base leading-none block">{mine.chip.primary}</span>
                  <span className="text-[12px] text-mute block mt-0.5">{mine.chip.secondary}</span>
                </span>
              )
            })()}
          </div>
        ))
      )}

      <SideMatchStrip doc={doc} roundId={round.id} onOpen={setSideBoardId} />

      {/*
        Two rows, not one squeeze. Five controls shared one line and at phone
        width the two selects were crushed to "SUN · MA" and "5:" — a picker
        you cannot read is a picker you tap on faith. The WHERE (round, tee
        time) gets the top row at full width in the field system's clothes;
        the DOERS (card photo, rules, add a nine) sit under it.
      */}
      <div className="grid gap-2 px-4 py-3 border-b border-rule">
        <div className="flex gap-2">
          <select
            value={round.id}
            onChange={(e) => {
              setRoundId(e.target.value)
              setHole(0)
            }}
            aria-label="Which round"
            className="bc-field bc-label bc-tap flex-1 w-full"
          >
            {inPlayOrder(doc.rounds).map((r) => (
              <option key={r.id} value={r.id}>
                {roundPickerLabel(doc, r)}
              </option>
            ))}
          </select>
          <select
            value={group.id}
            onChange={(e) => setGroupId(e.target.value)}
            aria-label="Which tee time"
            className="bc-field bc-label bc-tap flex-[0_0_7.5rem]"
          >
            {groups.map((g, i) => (
              <option key={g.id} value={g.id}>
                {clockTime(g.teeTime) || `Group ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          {/*
            The photographed pro-shop card, for when the app's numbers are
            being argued with. Course-scoped like the selects above it.
          */}
          <CardPhotoButton courseId={course.id} courseName={course.name} />
          {match && (
            <button
              type="button"
              onClick={() => setRulesOpen((v) => !v)}
              aria-expanded={rulesOpen}
              aria-label="How this round is scored"
              className="bc-tap bc-label border border-ink px-4 py-2 bg-paper shrink-0"
            >
              {/* R for rules. It was a lower-case "i", which in this row of
                  worded controls read as a stray character rather than a button —
                  and an italic serif i beside "+ Round" looked like a typo. */}
              R
            </button>
          )}
          {/* An extra nine, decided on a tee. */}
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="bc-tap bc-label border border-ink px-3 py-2 bg-paper whitespace-nowrap flex-1"
          >
            + Round
          </button>
        </div>
      </div>

      {rulesOpen && match && (
        <RoundRules doc={doc} match={match} onClose={() => setRulesOpen(false)} />
      )}

      <MissingScores
        doc={doc}
        ctx={ctx}
        playerIds={group.playerIds}
        holes={holes}
        onGo={(i) => {
          setHole(i)
          setPickerOpen(false)
        }}
      />

      <ConfirmCard
        doc={doc}
        ctx={ctx}
        group={group}
        roundLabel={round.label}
        atTheEnd={hole >= holes.length - 1}
        counts={!match || matchCounts(match)}
        called={match?.calledAt != null}
        onGoToHole={(i) => {
          setHole(i)
          setPickerOpen(false)
        }}
      />

    </>
  )
}
